import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material';

import { usePaymentMethod, useSubscription } from '@mopla/business-logic';
import { ESubscriptionStatus, PaymentMethodType } from '@mopla/data-models';
import { passengerThemeV2 } from '@mopla/mui';
import { usePaymentPortalControls } from '@mopla/payment-flow';
import { PaymentPortal } from '@mopla/payment-flow';
import { DTicketPageLayout, MplButton, MplErrorModal } from '@mopla/ui';

import { usePassengerAppNavigate } from '../../../hooks';

import { EditPaymentMethodOverview } from './EditPaymentMethodOverview';
import { EditPaymentMethodSelection } from './EditPaymentMethodSelection';

const RESTORE_PAGE = '/user-dticket/edit-payment-method';

type TEditPaymentStep = 'overview' | 'selection';

export const EditPaymentMethod = () => {
	const { t } = useTranslation('editPaymentMethod');
	const { navigateToUserDTicket } = usePassengerAppNavigate();
	const { subscriptionData: subscription } = useSubscription();

	const [step, setStep] = useState<TEditPaymentStep>('overview');
	const [isError, setIsError] = useState(false);

	const { openPaymentPortal, isPaymentPortalOpened, setPaymentStep } =
		usePaymentPortalControls();

	const {
		defaultPaymentMethod,
		isPaymentMethodLoading,
		isPaymentMethodSaving,
		refetchPaymentMethod,
	} = usePaymentMethod();

	useEffect(() => {
		refetchPaymentMethod();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// When the payment portal closes (e.g. after Stripe card flow), reset to overview
	useEffect(() => {
		if (!isPaymentPortalOpened && step === 'selection') {
			setStep('overview');
			refetchPaymentMethod();
		}
	}, [isPaymentPortalOpened]); // eslint-disable-line react-hooks/exhaustive-deps

	const isLoading = isPaymentMethodSaving || isPaymentMethodLoading;

	const isPendingSubscription =
		subscription?.status === ESubscriptionStatus.PENDING;

	const handleEditClick = () => {
		if (isPendingSubscription) return;
		setStep('selection');
	};

	const handleSelectionBack = () => setStep('overview');

	const handleSelectionSubmit = useCallback(
		(paymentMethod: PaymentMethodType) => {
			const paymentStep = paymentMethod === 'sepa' ? 'sepaRedirect' : 'stripe';
			openPaymentPortal();
			setPaymentStep(paymentStep);
		},
		[openPaymentPortal, setPaymentStep]
	);

	const isSelectionStep = step === 'selection';

	return (
		<ThemeProvider theme={passengerThemeV2}>
			{isSelectionStep ? (
				<EditPaymentMethodSelection
					onBack={handleSelectionBack}
					onSubmit={handleSelectionSubmit}
				/>
			) : (
				<DTicketPageLayout
					title={t('page_title')}
					onBack={navigateToUserDTicket}
					scrollable={true}
					dataTestId="edit-payment-method-page"
				>
					<MplErrorModal
						open={isError}
						testId="EditPaymentMethod-MplModal-isError"
						buttons={
							<MplButton fullWidth onClick={() => setIsError(false)}>
								{t('error_close')}
							</MplButton>
						}
					/>

					<EditPaymentMethodOverview
						isLoading={isLoading}
						defaultPaymentMethod={defaultPaymentMethod}
						isPendingSubscription={isPendingSubscription}
						onEdit={handleEditClick}
						onAddPaymentMethod={handleEditClick}
					/>
				</DTicketPageLayout>
			)}

			{isPaymentPortalOpened && (
				<PaymentPortal restorePage={RESTORE_PAGE} restoreQuery="" />
			)}
		</ThemeProvider>
	);
};
